import { Component, computed, input, output } from '@angular/core';

export interface PaginationEllipsis {
  ellipsis: true;
  target: number;
}

export type PaginationItem = number | PaginationEllipsis;

const SIBLING_COUNT = 1;

function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination-component.html',
  styleUrls: ['./pagination-component.css'],
  standalone: true,
})
export class PaginationComponent {
  public currentPage = input.required<number>();
  public totalPages = input.required<number>();

  public theme = input<'store' | 'admin'>('store');

  public pageChange = output<number>();

  public pagesArray = computed<PaginationItem[]>(() => {
    const total = this.totalPages();
    const current = this.currentPage();

    const totalVisiblePages = SIBLING_COUNT * 2 + 5;
    if (total <= totalVisiblePages) {
      return range(1, total);
    }

    const leftSiblingIndex = Math.max(current - SIBLING_COUNT, 1);
    const rightSiblingIndex = Math.min(current + SIBLING_COUNT, total);

    const shouldShowLeftEllipsis = leftSiblingIndex > 2;
    const shouldShowRightEllipsis = rightSiblingIndex < total - 1;

    if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
      const leftItemCount = 3 + SIBLING_COUNT * 2;
      return [...range(1, leftItemCount), { ellipsis: true, target: total }, total];
    }

    if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
      const rightItemCount = 3 + SIBLING_COUNT * 2;
      return [1, { ellipsis: true, target: 1 }, ...range(total - rightItemCount + 1, total)];
    }

    return [
      1,
      { ellipsis: true, target: 1 },
      ...range(leftSiblingIndex, rightSiblingIndex),
      { ellipsis: true, target: total },
      total,
    ];
  });

  public isEllipsis(item: PaginationItem): item is PaginationEllipsis {
    return typeof item !== 'number';
  }

  public onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages() && page !== this.currentPage()) {
      this.pageChange.emit(page);
    }
  }
}
